import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";
import Feedback from "./Feedback";

const statutColors = {
  en_attente: "warning",
  confirmee: "success",
  refusee: "error",
  archivee: "default",
};

const statutLabels = {
  en_attente: "En attente",
  confirmee: "Confirmée",
  refusee: "Refusée",
  archivee: "Archivée",
};

const statutBgColors = {
  en_attente: "#fb8c00",
  confirmee: "#43a047",
  refusee: "#e53935",
  archivee: "#757575",
};

const steps = ["Informations générales", "Responsables", "Équipes", "Chants & Divers"];

const defaultForm = {
  edition_id: "", groupe_id: "", occasion_id: "", date: "", heure: "", lieu: "", notes: "",
  occasion_education: "", occasion_autre: "", lieu_type: "", lieu_precision: "",
  nb_personnes: "", responsable_fete: "", responsable_adjoint1: "", responsable_adjoint2: "",
  presentateur1: "", presentateur2: "", langue_chants: "", groupe_local: false,
  groupe_ville_origine: "", materiel_audio: false, publicite_radio: false,
  recitateur1: "", recitateur2: "", cadeaux: "", equipe_reception: "",
  equipe_securite: "", equipe_service: "", equipe_nettoyage: "", equipe_vente: "",
  equipe_restauration: "", responsable_table: "",
};

function Step1({ form, setForm, groupes, occasions, editions }) {
  const f = (field) => ({
    value: form[field],
    onChange: (e) => setForm((prev) => ({ ...prev, [field]: e.target.value })),
  });
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField label="Édition" select fullWidth required
          sx={{ "& .MuiInputBase-root": { height: 56 } }}
          value={form.edition_id}
          onChange={(e) => setForm((prev) => ({ ...prev, edition_id: e.target.value }))}>
          {editions.map((e) => <MenuItem key={e.id} value={e.id}>{e.libelle}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Groupe de chants" select fullWidth required
          sx={{ "& .MuiInputBase-root": { height: 56 } }}
          value={form.groupe_id}
          onChange={(e) => setForm((prev) => ({ ...prev, groupe_id: e.target.value }))}>
          {groupes.map((g) => (
            <MenuItem key={g.id} value={g.id}>
              {g.nom} — {Array.isArray(g.type_chants) ? g.type_chants.join(", ") : g.type_chants}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Occasion" select fullWidth required
          sx={{ "& .MuiInputBase-root": { height: 56 } }}
          value={form.occasion_id}
          onChange={(e) => setForm((prev) => ({ ...prev, occasion_id: e.target.value }))}>
          {occasions.map((o) => <MenuItem key={o.id} value={o.id}>{o.libelle}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Date" type="date" fullWidth required InputLabelProps={{ shrink: true }} {...f("date")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Heure" type="time" fullWidth InputLabelProps={{ shrink: true }} {...f("heure")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Nombre de personnes attendues" type="number" fullWidth {...f("nb_personnes")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <FormLabel>Type de lieu</FormLabel>
          <RadioGroup row {...f("lieu_type")}>
            <FormControlLabel value="Salle extérieure" control={<Radio />} label="Salle extérieure" />
            <FormControlLabel value="Centre LACM" control={<Radio />} label="Centre LACM" />
            <FormControlLabel value="Autre" control={<Radio />} label="Autre" />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Lieu précis" fullWidth required {...f("lieu")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Détails supplémentaires sur le lieu" fullWidth multiline rows={2} {...f("lieu_precision")} />
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <FormLabel>Occasion liée à l&apos;Education</FormLabel>
          <RadioGroup row {...f("occasion_education")}>
            <FormControlLabel value="Fin de l'année scolaire" control={<Radio />} label="Fin de l'année scolaire" />
            <FormControlLabel value="Fêtes des diplômés" control={<Radio />} label="Fêtes des diplômés" />
            <FormControlLabel value="Autre" control={<Radio />} label="Autre" />
          </RadioGroup>
        </FormControl>
      </Grid>
      {form.occasion_education === "Autre" && (
        <Grid item xs={12}>
          <TextField label="Préciser l'occasion" fullWidth {...f("occasion_autre")} />
        </Grid>
      )}
    </Grid>
  );
}

function Step2({ form, setForm }) {
  const f = (field) => ({
    value: form[field],
    onChange: (e) => setForm((prev) => ({ ...prev, [field]: e.target.value })),
  });
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField label="Responsable de la fête (nom, mail, téléphone)" fullWidth multiline rows={2} {...f("responsable_fete")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Responsable adjoint 1 (nom, mail, téléphone)" fullWidth multiline rows={2} {...f("responsable_adjoint1")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Responsable adjoint 2 (nom, mail, téléphone)" fullWidth multiline rows={2} {...f("responsable_adjoint2")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Premier présentateur" fullWidth {...f("presentateur1")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Deuxième présentateur (sinon N/A)" fullWidth {...f("presentateur2")} />
      </Grid>
      <Grid item xs={12}>
        <TextField label="Notes supplémentaires" fullWidth multiline rows={3} {...f("notes")} />
      </Grid>
    </Grid>
  );
}

function Step3({ form, setForm }) {
  const f = (field) => ({
    value: form[field],
    onChange: (e) => setForm((prev) => ({ ...prev, [field]: e.target.value })),
  });
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField label="Équipe de réception (responsable + membres)" fullWidth multiline rows={3} {...f("equipe_reception")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Équipe de sécurité (responsable + membres)" fullWidth multiline rows={3} {...f("equipe_securite")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Équipe de service (responsable + membres)" fullWidth multiline rows={3} {...f("equipe_service")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Équipe de nettoyage (responsable + membres)" fullWidth multiline rows={3} {...f("equipe_nettoyage")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Équipe de vente (responsable + membres)" fullWidth multiline rows={3} {...f("equipe_vente")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Équipe de restauration (responsable + membres)" fullWidth multiline rows={3} {...f("equipe_restauration")} />
      </Grid>
      <Grid item xs={12}>
        <TextField label="Responsable de la table principale" fullWidth {...f("responsable_table")} />
      </Grid>
    </Grid>
  );
}

function Step4({ form, setForm }) {
  const f = (field) => ({
    value: form[field],
    onChange: (e) => setForm((prev) => ({ ...prev, [field]: e.target.value })),
  });
  const sw = (field) => ({
    checked: form[field],
    onChange: (e) => setForm((prev) => ({ ...prev, [field]: e.target.checked })),
  });
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <FormLabel>Langue des chants souhaitée</FormLabel>
          <RadioGroup row {...f("langue_chants")}>
            <FormControlLabel value="Arabe" control={<Radio />} label="Arabe" />
            <FormControlLabel value="Français" control={<Radio />} label="Français" />
            <FormControlLabel value="Les deux" control={<Radio />} label="Les deux" />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel control={<Switch {...sw("groupe_local")} />} label="Groupe de chants local à la ville" />
        {!form.groupe_local && (
          <TextField label="Ville du groupe souhaité" fullWidth sx={{ mt: 2 }} {...f("groupe_ville_origine")} />
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Premier récitateur de Qour'an" fullWidth {...f("recitateur1")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Deuxième récitateur de Qour'an" fullWidth {...f("recitateur2")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Cadeaux à distribuer (sinon Non)" fullWidth {...f("cadeaux")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <MDBox display="flex" flexDirection="column" gap={1}>
          <FormControlLabel control={<Switch {...sw("materiel_audio")} />} label="Besoin de matériel audio" />
          <FormControlLabel control={<Switch {...sw("publicite_radio")} />} label="Publicité dans une radio" />
        </MDBox>
      </Grid>
    </Grid>
  );
}

function MesReservations() {
  const [reservations, setReservations] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [editions, setEditions] = useState([]);
  const [open, setOpen] = useState(false);
  const [openFiche, setOpenFiche] = useState(false);
  const [selectedFiche, setSelectedFiche] = useState(null);
  const [editId, setEditId] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [showArchivees, setShowArchivees] = useState(false);
  const [openFeedback, setOpenFeedback] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const fetchData = async () => {
    try {
      const [resRes, groupesRes, occasionsRes, editionsRes] = await Promise.all([
        api.get("/reservations"),
        api.get("/groupes"),
        api.get("/occasions"),
        api.get("/editions/actives"),
      ]);
      setReservations(resRes.data);
      setGroupes(groupesRes.data);
      setOccasions(occasionsRes.data);
      setEditions(editionsRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleOpen = () => {
    setForm(defaultForm);
    setEditId(null);
    setActiveStep(0);
    setOpen(true);
  };

  const handleEdit = (r) => {
    setForm({
      edition_id: r.edition_id || "",
      groupe_id: r.groupe_id || "",
      occasion_id: r.occasion_id || "",
      date: r.date ? r.date.slice(0, 10) : "",
      heure: r.heure ? r.heure.slice(0, 5) : "",
      lieu: r.lieu || "",
      notes: r.notes || "",
      occasion_education: r.occasion_education || "",
      occasion_autre: r.occasion_autre || "",
      lieu_type: r.lieu_type || "",
      lieu_precision: r.lieu_precision || "",
      nb_personnes: r.nb_personnes || "",
      responsable_fete: r.responsable_fete || "",
      responsable_adjoint1: r.responsable_adjoint1 || "",
      responsable_adjoint2: r.responsable_adjoint2 || "",
      presentateur1: r.presentateur1 || "",
      presentateur2: r.presentateur2 || "",
      langue_chants: r.langue_chants || "",
      groupe_local: r.groupe_local || false,
      groupe_ville_origine: r.groupe_ville_origine || "",
      materiel_audio: r.materiel_audio || false,
      publicite_radio: r.publicite_radio || false,
      recitateur1: r.recitateur1 || "",
      recitateur2: r.recitateur2 || "",
      cadeaux: r.cadeaux || "",
      equipe_reception: r.equipe_reception || "",
      equipe_securite: r.equipe_securite || "",
      equipe_service: r.equipe_service || "",
      equipe_nettoyage: r.equipe_nettoyage || "",
      equipe_vente: r.equipe_vente || "",
      equipe_restauration: r.equipe_restauration || "",
      responsable_table: r.responsable_table || "",
    });
    setEditId(r.id);
    setActiveStep(0);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm(defaultForm);
    setEditId(null);
    setActiveStep(0);
  };

  const handleOpenFiche = (r) => {
    setSelectedFiche(r);
    setOpenFiche(true);
  };

  const handleCloseFiche = () => {
    setOpenFiche(false);
    setSelectedFiche(null);
  };

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (editId) {
        await api.put(`/reservations/${editId}`, form);
      } else {
        await api.post("/reservations", form);
      }
      fetchData();
      handleClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Supprimer cette réservation ?")) {
      try {
        await api.delete(`/reservations/${id}`);
        fetchData();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const isStep1Valid = form.edition_id && form.groupe_id && form.occasion_id && form.date && form.lieu;

  const reservationsFiltrees = reservations.filter((r) =>
    showArchivees ? true : r.statut !== "archivee"
  );

  const stepContent = [
    <Step1 key="step1" form={form} setForm={setForm} groupes={groupes} occasions={occasions} editions={editions} />,
    <Step2 key="step2" form={form} setForm={setForm} />,
    <Step3 key="step3" form={form} setForm={setForm} />,
    <Step4 key="step4" form={form} setForm={setForm} />,
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2} mt={-3} py={3} px={2}
                variant="gradient" bgColor="info"
                borderRadius="lg" coloredShadow="info"
                display="flex" justifyContent="space-between" alignItems="center"
              >
                <MDTypography variant="h6" color="white">Mes réservations</MDTypography>
                <MDButton variant="outlined" color="white" size="small" onClick={handleOpen}>
                  <Icon>add</Icon>&nbsp;Nouvelle demande
                </MDButton>
              </MDBox>

              {/* Filtre archivées */}
              <MDBox px={3} pt={2} display="flex" alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={showArchivees}
                      onChange={(e) => setShowArchivees(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <MDTypography variant="caption" fontWeight="medium" color="text">
                      Afficher les demandes archivées
                    </MDTypography>
                  }
                />
              </MDBox>

              <MDBox pt={2} px={3} pb={3}>
                <Grid container spacing={3}>
                  {reservationsFiltrees.map((r) => (
                    <Grid item xs={12} md={6} lg={4} key={r.id}>
                      <Card sx={{ p: 2, border: "1px solid #eee", opacity: r.statut === "archivee" ? 0.7 : 1 }}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <MDTypography variant="h6">{r.groupe_nom}</MDTypography>
                          <Chip label={statutLabels[r.statut]} color={statutColors[r.statut]} size="small" />
                        </MDBox>
                        {r.edition_libelle && (
                          <Chip label={r.edition_libelle} size="small"
                            sx={{ bgcolor: "#e3f2fd", color: "#1565c0", mb: 1 }} />
                        )}
                        <MDTypography variant="caption" color="text" display="block">
                          Occasion : {r.occasion}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block">
                          Date : {new Date(r.date).toLocaleDateString("fr-FR")} à {r.heure?.slice(0, 5)}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block">
                          Lieu : {r.lieu}
                        </MDTypography>
                        {r.groupe_affecte_nom && (
                          <>
                            <Divider sx={{ my: 1 }} />
                            <MDTypography variant="caption" display="block" fontWeight="medium" sx={{ color: "#2e7d32" }}>
                              Groupe affecté : {r.groupe_affecte_nom}
                            </MDTypography>
                          </>
                        )}
                        {r.motif_refus && (
                          <MDTypography variant="caption" color="error" display="block">
                            Motif refus : {r.motif_refus}
                          </MDTypography>
                        )}
                        <MDBox mt={1} display="flex" gap={1} flexWrap="wrap">
                          <MDButton color="info" size="small" variant="outlined" onClick={() => handleOpenFiche(r)}>
                            <Icon>visibility</Icon>&nbsp;Voir
                          </MDButton>
                          {r.statut === "confirmee" && (
                            <MDButton
                              color="success"
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setSelectedFeedback(r);
                                setOpenFeedback(true);
                              }}
                            >
                              <Icon>rate_review</Icon>&nbsp;Retour
                            </MDButton>
                          )}
                          {r.statut === "en_attente" && (
                            <>
                              <MDButton color="info" size="small" variant="outlined" onClick={() => handleEdit(r)}>
                                <Icon>edit</Icon>&nbsp;Modifier
                              </MDButton>
                              <MDButton color="error" size="small" variant="outlined" onClick={() => handleDelete(r.id)}>
                                <Icon>delete</Icon>&nbsp;Supprimer
                              </MDButton>
                            </>
                          )}
                        </MDBox>
                      </Card>
                    </Grid>
                  ))}
                  {reservationsFiltrees.length === 0 && (
                    <Grid item xs={12}>
                      <MDTypography variant="body2" color="text" textAlign="center">
                        Aucune réservation pour le moment.
                      </MDTypography>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialog Formulaire */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>
          {editId ? "Modifier la demande de réservation" : "Nouvelle demande de réservation"}
        </DialogTitle>
        <DialogContent>
          <MDBox mb={3}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>
          </MDBox>
          <MDBox mt={2}>{stepContent[activeStep]}</MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose} color="secondary">Annuler</MDButton>
          {activeStep > 0 && (
            <MDButton onClick={handleBack} color="info" variant="outlined">Précédent</MDButton>
          )}
          {activeStep < steps.length - 1 ? (
            <MDButton onClick={handleNext} color="info" disabled={activeStep === 0 && !isStep1Valid}>
              Suivant
            </MDButton>
          ) : (
            <MDButton onClick={handleSubmit} color="info" disabled={loading}>
              {loading ? "Envoi..." : editId ? "Enregistrer les modifications" : "Envoyer la demande"}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog Fiche */}
      <Dialog open={openFiche} onClose={handleCloseFiche} fullWidth maxWidth="md">
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <span>Fiche de réservation</span>
            {selectedFiche && (
              <Chip
                label={statutLabels[selectedFiche.statut]}
                size="small"
                sx={{ bgcolor: statutBgColors[selectedFiche.statut], color: "white" }}
              />
            )}
          </MDBox>
        </DialogTitle>
        <DialogContent>
          {selectedFiche && (
            <MDBox display="flex" flexDirection="column" gap={2}>

              <MDBox sx={{ bgcolor: "#e3f2fd", borderRadius: 2, p: 2 }}>
                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#1565c0" }} mb={1}>
                  Informations générales
                </MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Édition :</strong> {selectedFiche.edition_libelle || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Occasion :</strong> {selectedFiche.occasion || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Date :</strong> {new Date(selectedFiche.date).toLocaleDateString("fr-FR")}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Heure :</strong> {selectedFiche.heure?.slice(0, 5) || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Nombre de personnes :</strong> {selectedFiche.nb_personnes || "—"}</MDTypography>
              </MDBox>

              <MDBox sx={{ bgcolor: "#f3e5f5", borderRadius: 2, p: 2 }}>
                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#6a1b9a" }} mb={1}>Lieu</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Type de lieu :</strong> {selectedFiche.lieu_type || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Lieu précis :</strong> {selectedFiche.lieu || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Détails :</strong> {selectedFiche.lieu_precision || "—"}</MDTypography>
              </MDBox>

              <MDBox sx={{ bgcolor: "#fff3e0", borderRadius: 2, p: 2 }}>
                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#e65100" }} mb={1}>
                  Occasion éducative
                </MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Type :</strong> {selectedFiche.occasion_education || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Précision :</strong> {selectedFiche.occasion_autre || "—"}</MDTypography>
              </MDBox>

              <MDBox sx={{ bgcolor: "#e8f5e9", borderRadius: 2, p: 2 }}>
                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#2e7d32" }} mb={1}>
                  Groupe de chants
                </MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Groupe demandé :</strong> {selectedFiche.groupe_nom || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Langue souhaitée :</strong> {selectedFiche.langue_chants || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Groupe local :</strong> {selectedFiche.groupe_local ? "Oui" : "Non"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Ville du groupe :</strong> {selectedFiche.groupe_ville_origine || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Matériel audio :</strong> {selectedFiche.materiel_audio ? "Oui" : "Non"}</MDTypography>
                {selectedFiche.groupe_affecte_nom && (
                  <MDTypography variant="body2" mb={0.5} sx={{ color: "#1b5e20" }}>
                    <strong>Groupe affecté :</strong> {selectedFiche.groupe_affecte_nom}
                  </MDTypography>
                )}
              </MDBox>

              <MDBox sx={{ bgcolor: "#fce4ec", borderRadius: 2, p: 2 }}>
                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#880e4f" }} mb={1}>
                  Responsables et présentateurs
                </MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Responsable fête :</strong> {selectedFiche.responsable_fete || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Responsable adjoint 1 :</strong> {selectedFiche.responsable_adjoint1 || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Responsable adjoint 2 :</strong> {selectedFiche.responsable_adjoint2 || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Présentateur 1 :</strong> {selectedFiche.presentateur1 || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Présentateur 2 :</strong> {selectedFiche.presentateur2 || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Responsable table :</strong> {selectedFiche.responsable_table || "—"}</MDTypography>
              </MDBox>

              <MDBox sx={{ bgcolor: "#e0f2f1", borderRadius: 2, p: 2 }}>
                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#004d40" }} mb={1}>Équipes</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Réception :</strong> {selectedFiche.equipe_reception || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Sécurité :</strong> {selectedFiche.equipe_securite || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Service :</strong> {selectedFiche.equipe_service || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Nettoyage :</strong> {selectedFiche.equipe_nettoyage || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Vente :</strong> {selectedFiche.equipe_vente || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Restauration :</strong> {selectedFiche.equipe_restauration || "—"}</MDTypography>
              </MDBox>

              <MDBox sx={{ bgcolor: "#fff8e1", borderRadius: 2, p: 2 }}>
                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#f57f17" }} mb={1}>Divers</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Récitateur 1 :</strong> {selectedFiche.recitateur1 || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Récitateur 2 :</strong> {selectedFiche.recitateur2 || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Cadeaux :</strong> {selectedFiche.cadeaux || "—"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Publicité radio :</strong> {selectedFiche.publicite_radio ? "Oui" : "Non"}</MDTypography>
                <MDTypography variant="body2" mb={0.5}><strong>Notes :</strong> {selectedFiche.notes || "—"}</MDTypography>
              </MDBox>

              {selectedFiche.motif_refus && (
                <MDBox sx={{ bgcolor: "#ffebee", borderRadius: 2, p: 2 }}>
                  <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#c62828" }} mb={1}>
                    Motif de refus
                  </MDTypography>
                  <MDTypography variant="body2" sx={{ color: "#b71c1c" }}>{selectedFiche.motif_refus}</MDTypography>
                </MDBox>
              )}

              <MDTypography variant="caption" color="text">
                Demande créée le : {new Date(selectedFiche.created_at).toLocaleDateString("fr-FR")}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseFiche} color="info">Fermer</MDButton>
        </DialogActions>
      </Dialog>

      <Feedback
        open={openFeedback}
        onClose={() => {
          setOpenFeedback(false);
          setSelectedFeedback(null);
        }}
        reservation={selectedFeedback}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default MesReservations;